import React, { useEffect } from 'react';
import { Home, Search, TrendingUp, Heart, Key, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import { useCmsContent } from '../../hooks/useCmsContent';

type FeatureProps = {
  icon: React.ElementType;
  title: string;
  description: string;
  delay?: number;
  isSecondary?: boolean;
};

const Feature = ({ icon: Icon, title, description, delay = 0, isSecondary = false }: FeatureProps) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.8, delay }}
    whileHover={{ y: -10 }}
    className={`bg-white p-10 rounded-lg shadow-lg hover:shadow-xl transition-all border-t-4 ${isSecondary ? 'border-secondary' : 'border-primary'}`}
  >
    <div className={`${isSecondary ? 'bg-secondary/10' : 'bg-primary/10'} w-20 h-20 rounded-full flex items-center justify-center mb-8`}>
      <Icon className={`h-10 w-10 ${isSecondary ? 'text-secondary' : 'text-primary'}`} />
    </div>
    <h3 className={`text-2xl font-semibold mb-4 ${isSecondary ? 'text-secondary' : 'text-primary'}`}>{title}</h3>
    <p className="text-gray-600 text-lg leading-relaxed">
      {description}
    </p>
  </motion.div>
);

const FeaturesSection = () => {
  const { getContentValue, loading } = useCmsContent({ section: 'features' });
  
  return (
    <section className="py-24 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <span className="text-secondary uppercase tracking-wider font-semibold">I Nostri Punti di Forza</span>
          <h2 className="text-4xl font-monomakh text-primary mt-2 mb-4">Perché Scegliere Noi</h2>
          <p className="text-gray-600 mt-4 max-w-2xl mx-auto text-lg">
            Da oltre {getContentValue('anni_esperienza') || '8'} anni aiutiamo le persone a trovare la casa dei loro sogni, 
            con passione, professionalità e un'attenzione unica ai dettagli.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-10">
          <Feature 
            icon={Heart} 
            title="Consulenza Personalizzata" 
            description="Ascoltiamo attentamente le tue esigenze e desideri per guidarti verso l'immobile perfetto che rispecchia il tuo stile di vita e le tue aspirazioni."
          />
          
          <Feature 
            icon={Key} 
            title="Soluzioni Chiavi in Mano" 
            description="Ti accompagniamo in ogni fase della compravendita, dalla ricerca alla consegna delle chiavi, gestendo tutti gli aspetti burocratici e legali."
            delay={0.2}
            isSecondary={true}
          />
          
          <Feature 
            icon={Users} 
            title="Professionisti del Territorio" 
            description="Conosciamo ogni angolo di Scandicci e delle zone limitrofe. La nostra esperienza locale ti garantisce la migliore valutazione del mercato."
            delay={0.4}
          />
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;